import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import MessageSidebar from "@/components/MessageSidebar";

// Public Pages
import LandingPage from "@/pages/LandingPage";
import AboutPage from "@/pages/AboutPage";
import ProjectsPage from "@/pages/ProjectsPage";
import JoinUsPage from "@/pages/JoinUsPage";
import MeetTeamPage from "@/pages/MeetTeamPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import StudentRegisterPage from "@/pages/StudentRegisterPage";
import StudentLoginPage from "@/pages/StudentLoginPage";
import TutorLoginPage from "@/pages/TutorLoginPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import NotFoundPage from "@/pages/NotFoundPage";

// Student Pages
import StudentDashboard from "@/pages/student/StudentDashboard";
import CourseCatalog from "@/pages/student/CourseCatalog";
import CourseDetail from "@/pages/student/CourseDetail";
import AssignmentsPage from "@/pages/student/AssignmentsPage";
import GamePage from "@/pages/student/GamePage";

// Tutor Pages
import TutorDashboard from "@/pages/tutor/TutorDashboard";
import TutorSetupPage from "@/pages/tutor/TutorSetupPage";

// Admin Pages
import AdminDashboard from "@/pages/AdminDashboard";

// Protected Routes
const StudentRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><div className="spinner"></div></div>;
  if (!user || user.role !== "student") return <Navigate to="/login" replace />;
  return children;
};

const TutorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><div className="spinner"></div></div>;
  if (!user || user.role !== "tutor") return <Navigate to="/tutor/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]"><div className="spinner"></div></div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin-vv-secret-access-2026" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/meet-our-team" element={<MeetTeamPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/join-us" element={<JoinUsPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/register" element={<StudentRegisterPage />} />
            <Route path="/login" element={<StudentLoginPage />} />
            <Route path="/tutor/login" element={<TutorLoginPage />} />
            <Route path="/admin-vv-secret-access-2026" element={<AdminLoginPage />} />
            {/* Old admin login path is intentionally hidden — 404 for everyone */}
            <Route path="/admin/login" element={<NotFoundPage />} />
            
            {/* Student Routes */}
            <Route path="/dashboard" element={<StudentRoute><StudentDashboard /></StudentRoute>} />
            <Route path="/courses" element={<StudentRoute><CourseCatalog /></StudentRoute>} />
            <Route path="/courses/:courseId" element={<StudentRoute><CourseDetail /></StudentRoute>} />
            <Route path="/assignments/:enrollmentId" element={<StudentRoute><AssignmentsPage /></StudentRoute>} />
            <Route path="/game/:enrollmentId/:weekNumber" element={<StudentRoute><GamePage /></StudentRoute>} />
            
            {/* Tutor Routes */}
            <Route path="/tutor/setup" element={<TutorRoute><TutorSetupPage /></TutorRoute>} />
            <Route path="/tutor/*" element={<TutorRoute><TutorDashboard /></TutorRoute>} />
            
            {/* Admin Routes */}
            <Route path="/admin/*" element={<AdminRoute><AdminDashboard /></AdminRoute>} />

            {/* Catch-all 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
          <MessageSidebar />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

export default App;
